import { useEffect, useMemo, useRef, useState } from "react";
import b from "../../../services/api/Boards";
import l from "../../../services/api/Landings";


export function useCRMLeadTable({questId, transform, filters, shareToken = "" }) {
  const [pagination, setPagination] = useState({
    pageNo: 0,
    numOfDocuments: 10,
    leads: [],
    loading: false,
    hasMore: true,
    error: false,
    retryPage: null,
  });

  const [leadColumnMap, setLeadColumnMap] = useState({});
  const request = useRef({ id: 0, generation: 0, pending: false, pageNo: -1, hasMore: true });
  const multipleCustomFields = useMemo(() => transform(pagination.leads), [pagination.leads, transform]);

  useEffect(() => () => {
    request.current.id += 1;
    request.current.generation += 1;
  }, []);

  const loadLeadColumnMapping = async (leads, generation) => {
    try {
      const mapping = await b.getLeadColumns(questId, leads.map(lead => lead._id), shareToken);
      if (request.current.generation === generation) {
        setLeadColumnMap(previous => ({ ...previous, ...(mapping?.data?.map || {}) }));
      }
    } catch { /* Keep the rows even if column labels are unavailable. */ }
  };

  const loadLeadBatch = async (pageNo = 0) => {
    if (!questId) return;
    if (pageNo > 0 && (request.current.pending || !request.current.hasMore || pageNo !== request.current.pageNo + 1)) return;
    const requestId = ++request.current.id;
    request.current.pending = true;
    if (pageNo === 0) {
      request.current.generation += 1;
      request.current.pageNo = -1;
      request.current.hasMore = true;
    }
    const generation = request.current.generation;
    setPagination(previous => ({ ...previous, loading: true, error: false, retryPage: null }));

    try {
      const res = await l.getPaginatedLeads(
        questId, pageNo, pagination.numOfDocuments,
        filters.customRange?.start, filters.customRange?.end,
        filters.callFilters, filters.sourceFilter
      );
      if (request.current.id !== requestId) return;
      if (!res || res.status >= 400 || !Array.isArray(res.data?.leads)) throw new Error('Could not load leads');

      const incoming = res.data.leads;
      const hasMore = incoming.length >= pagination.numOfDocuments;
      request.current.pageNo = pageNo;
      request.current.hasMore = hasMore;
      setPagination(previous => {
        const existing = pageNo === 0 ? [] : previous.leads;
        const seen = new Set(existing.map(lead => String(lead._id || lead.id)));
        const added = incoming.filter(lead => {
          const id = lead._id || lead.id;
          if (!id) return true;
          if (seen.has(String(id))) return false;
          seen.add(String(id));
          return true;
        });
        return { ...previous, pageNo, leads: [...existing, ...added], hasMore };
      });

      if (pageNo === 0) setLeadColumnMap({});
      // Optional labels load independently, without holding up the next lead page.
      if (incoming.length) loadLeadColumnMapping(incoming, generation);
    } catch {
      if (request.current.id === requestId) {
        setPagination(previous => ({ ...previous, error: true, retryPage: pageNo }));
      }
    } finally {
      if (request.current.id === requestId) {
        request.current.pending = false;
        setPagination(previous => ({ ...previous, loading: false }));
      }
    }
  };

  return {
    pagination,
    loadLeadBatch,
    multipleCustomFields,
    leadColumnMap,
    setLeadColumnMap,
    setPagination
  };
}
